import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/features/auth/stores/authStore'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
type Connection = {
  id: string
  username: string
  revision: number
  stories: boolean
  status: string
}
type Film = {
  id: string
  title: string
  recipeSha256: string
  videos: { sha256: string }[]
  durationSec: number
}
type Release = {
  id: string
  title: string
  username: string
  placement: string
  scheduled_at: string
  timezone: string
  status: string
  media_id?: string
  error?: string
}
const base = '/api/v1/film-social'
const field = 'mt-1 min-h-11 w-full rounded-lg border bg-background px-3 text-sm'
export function ReleasePanel() {
  const owner = useAuthStore((s) => s.user?.id)
  const [params] = useSearchParams()
  const qc = useQueryClient()
  const queue = useQuery({
    queryKey: ['film-releases', owner],
    queryFn: async () =>
      (await apiClient.get<{ enabled: boolean; releases: Release[] }>(`${base}/releases`)).data,
    retry: false,
    refetchInterval: 15000,
  })
  const connections = useQuery({
    queryKey: ['release-connections', owner],
    queryFn: async () =>
      (await apiClient.get<{ connections: Connection[] }>(`${base}/connections`)).data.connections,
    retry: false,
  })
  const films = useQuery({
    queryKey: ['release-films', owner],
    queryFn: async () => (await apiClient.get<{ jobs: Film[] }>(`${base}/films`)).data.jobs,
    retry: false,
  })
  const [open, setOpen] = useState(false)
  const [jobId, setJobId] = useState(params.get('job') || '')
  const [connectionId, setConnectionId] = useState('')
  const [placement, setPlacement] = useState('REELS')
  const [at, setAt] = useState('')
  const [caption, setCaption] = useState('')
  const [approved, setApproved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [cancelId, setCancelId] = useState('')
  const request = useRef<{ body: string; key: string } | null>(null)
  const account = connections.data?.find((c) => c.id === connectionId && c.status === 'connected')
  const film = films.data?.find((f) => f.id === jobId && f.videos.length === 1)
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const resetApproval = () => setApproved(false)
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border bg-card p-5">
        <div>
          <h2 className="text-xl font-semibold">Instagram releases</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Review a finished film, then choose its account, Reel or Story and release time. A saved
            release is an explicit posting approval—not a render schedule.
          </p>
          <p className="mt-2 text-sm">
            {queue.data
              ? queue.data.enabled
                ? 'Human-approved delivery enabled. Unapproved films are never posted.'
                : 'Delivery paused. Existing release records remain visible.'
              : 'Checking delivery service…'}
          </p>
        </div>
        <Button
          className="min-h-11"
          disabled={!queue.data?.enabled || !films.data?.length}
          onClick={() => {
            setNotice('')
            resetApproval()
            setOpen(true)
          }}
        >
          Schedule approved film
        </Button>
      </div>
      {(queue.error || films.error || connections.error) && (
        <p role="alert" className="rounded-xl border p-4">
          Release tools unavailable. Retry after checking your sign-in and service configuration.
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-xl border p-4 text-sm">
          {notice}
        </p>
      )}
      {queue.data && !queue.data.releases.length && (
        <div className="rounded-xl border border-dashed p-6">
          <p className="font-medium">No social releases scheduled</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Approve an encoded film in Films first. Artwork idea dates do not schedule posts.
          </p>
        </div>
      )}
      <div className="space-y-3">
        {queue.data?.releases.map((r) => (
          <article key={r.id} className="rounded-xl border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold">{r.title}</h3>
                <p className="mt-1 text-sm">
                  @{r.username} · {r.placement === 'STORIES' ? 'Story' : 'Reel'} ·{' '}
                  {new Date(r.scheduled_at).toLocaleString(undefined, { timeZone: r.timezone })} (
                  {r.timezone})
                </p>
                <p className="mt-2 text-sm font-medium">{r.status.replace(/_/g, ' ')}</p>
              </div>
              {['scheduled', 'processing'].includes(r.status) && (
                <Button variant="outline" onClick={() => setCancelId(r.id)}>
                  Cancel release
                </Button>
              )}
            </div>
            {r.error && <p className="mt-3 text-sm text-amber-800">{r.error}</p>}
            {r.media_id && (
              <p className="mt-2 break-all text-xs text-muted-foreground">
                Meta publication receipt: {r.media_id}
              </p>
            )}
          </article>
        ))}
      </div>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!busy) setOpen(v)
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approve & schedule publication</DialogTitle>
            <DialogDescription>
              This will publish the exact selected film to the selected Instagram account at the
              chosen time. Changes require a new release approval.
            </DialogDescription>
          </DialogHeader>
          {notice && (
            <p role="alert" className="rounded-xl border p-3 text-sm">
              {notice}
            </p>
          )}
          <label className="text-sm">
            Approved film
            <select
              aria-label="Release film"
              className={field}
              value={jobId}
              onChange={(e) => {
                setJobId(e.target.value)
                resetApproval()
              }}
            >
              <option value="">Choose an approved film</option>
              {films.data
                ?.filter((f) => f.videos.length === 1)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title} · {f.durationSec}s
                  </option>
                ))}
            </select>
          </label>
          <label className="text-sm">
            Instagram account
            <select
              aria-label="Release account"
              className={field}
              value={connectionId}
              onChange={(e) => {
                setConnectionId(e.target.value)
                setPlacement('REELS')
                resetApproval()
              }}
            >
              <option value="">Choose a connected account</option>
              {connections.data
                ?.filter((c) => c.status === 'connected')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    @{c.username}
                  </option>
                ))}
            </select>
          </label>
          <label className="text-sm">
            Placement
            <select
              aria-label="Release placement"
              className={field}
              value={placement}
              onChange={(e) => {
                setPlacement(e.target.value)
                setCaption('')
                resetApproval()
              }}
            >
              <option value="REELS">Reel</option>
              <option
                value="STORIES"
                disabled={!account?.stories || !film || film.durationSec > 60}
              >
                Story · requires verified Business account; max 60s
              </option>
            </select>
          </label>
          {account && !account.stories && (
            <p className="text-xs text-amber-800">
              Stories are blocked: Meta has not verified this account’s Business type. Reels can
              still be scheduled after review.
            </p>
          )}
          <label className="text-sm">
            Release date & time ({zone})
            <input
              aria-label="Release date and time"
              type="datetime-local"
              className={field}
              value={at}
              onChange={(e) => {
                setAt(e.target.value)
                resetApproval()
              }}
            />
          </label>
          {placement === 'REELS' && (
            <label className="text-sm">
              Reel caption
              <textarea
                aria-label="Release caption"
                className={`${field} min-h-24 py-2`}
                maxLength={2200}
                value={caption}
                onChange={(e) => {
                  setCaption(e.target.value)
                  resetApproval()
                }}
              />
            </label>
          )}
          <label className="flex gap-3 rounded-xl border p-3 text-sm">
            <input
              aria-label="Approve this exact Instagram release"
              type="checkbox"
              checked={approved}
              onChange={(e) => setApproved(e.target.checked)}
            />
            <span>
              I reviewed this exact film and approve posting it to{' '}
              {account ? `@${account.username}` : 'the selected account'} as a{' '}
              {placement === 'STORIES' ? 'Story' : 'Reel'} at the time above.
            </span>
          </label>
          <p className="text-xs text-muted-foreground">
            The server checks current film/world approvals, account grant, actual MP4 codecs and
            immutable bytes. Unknown delivery outcomes stop for reconciliation, not automatic
            reposting.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                !queue.data?.enabled ||
                !approved ||
                !film ||
                !account ||
                !at ||
                (placement === 'STORIES' && !account.stories)
              }
              onClick={async () => {
                if (!film || !account) return
                const releaseTime = new Date(at)
                if (!Number.isFinite(releaseTime.getTime())) {
                  setNotice('Choose a valid release date and time.')
                  return
                }
                const body = {
                  jobId: film.id,
                  recipeSha256: film.recipeSha256,
                  artifactSha256: film.videos[0].sha256,
                  connectionId: account.id,
                  connectionRevision: account.revision,
                  placement,
                  caption: placement === 'STORIES' ? '' : caption,
                  scheduledAt: releaseTime.toISOString(),
                  timezone: zone,
                  releaseApproved: true,
                }
                const key = JSON.stringify(body)
                if (request.current?.body !== key)
                  request.current = { body: key, key: crypto.randomUUID() }
                setBusy(true)
                setNotice('')
                try {
                  await apiClient.post(`${base}/releases`, body, {
                    headers: { 'Idempotency-Key': request.current.key },
                  })
                  setOpen(false)
                  setNotice(
                    'Release approved and scheduled. Delivery begins at the chosen time; watch its status here.'
                  )
                  await qc.invalidateQueries({ queryKey: ['film-releases'] })
                } catch (e) {
                  setNotice(
                    (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
                      'Scheduling outcome unavailable. Retry unchanged to reuse the same request key.'
                  )
                } finally {
                  setBusy(false)
                }
              }}
            >
              {busy ? 'Verifying MP4…' : 'Approve & schedule post'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!cancelId}
        onOpenChange={(v) => {
          if (!v && !busy) setCancelId('')
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this release?</DialogTitle>
            <DialogDescription>
              This stops the pending social release, not the world or film. A publication already in
              progress cannot be undone here.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setCancelId('')}>
              Keep release
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  await apiClient.post(`${base}/releases/${cancelId}/cancel`)
                  setCancelId('')
                  await qc.invalidateQueries({ queryKey: ['film-releases'] })
                } catch {
                  setNotice(
                    'Cancellation unavailable: delivery may already be active. Inspect the current release state.'
                  )
                } finally {
                  setBusy(false)
                }
              }}
            >
              Confirm cancellation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
